// Atlas vegetation (ATL-4). PURE (ENG-1).
//
// Poisson-disc tree scatter (Bridson, k=30) inside the city's green polygons, plus
// any explicit OSM tree points. Deterministic from the city + seed so the main
// thread, the gen worker, and the server place identical trees. Points are turned
// into geometry (5-log trunk + 3×3×3 canopy with ±1 jitter) by the rasterizer.
import { Rng, defineSalt } from '../engine/rng';
import {
  boundsOf, pointInPolygonWithHoles,
  type IntermediateCity, type Pt, type BBox,
} from './intermediate';

const S_TREES = defineSalt('atlas-trees');
const S_TREE_SHAPE = defineSalt('atlas-tree-shape');

/** Poisson-disc sampling (Bridson) over `bb`, keeping only `accept`ed points at
 *  least `r` apart. Deterministic given `rng`. */
export function poissonDisk(bb: BBox, r: number, rng: Rng, accept: (x: number, z: number) => boolean, k = 30): Pt[] {
  const w = bb.maxX - bb.minX, h = bb.maxZ - bb.minZ;
  if (w <= 0 || h <= 0) return [];
  const cell = r / Math.SQRT2;
  const gw = Math.max(1, Math.ceil(w / cell)), gh = Math.max(1, Math.ceil(h / cell));
  const grid = new Int32Array(gw * gh).fill(-1);
  const pts: [number, number][] = [];
  const active: number[] = [];
  const gx = (x: number): number => Math.min(gw - 1, Math.max(0, Math.floor((x - bb.minX) / cell)));
  const gz = (z: number): number => Math.min(gh - 1, Math.max(0, Math.floor((z - bb.minZ) / cell)));

  const tryAdd = (x: number, z: number): boolean => {
    if (!accept(x, z)) return false;
    const cx = gx(x), cz = gz(z);
    for (let j = Math.max(0, cz - 2); j <= Math.min(gh - 1, cz + 2); j++) {
      for (let i = Math.max(0, cx - 2); i <= Math.min(gw - 1, cx + 2); i++) {
        const idx = grid[j * gw + i]!;
        if (idx >= 0) { const p = pts[idx]!; if ((p[0] - x) ** 2 + (p[1] - z) ** 2 < r * r) return false; }
      }
    }
    grid[cz * gw + cx] = pts.length; active.push(pts.length); pts.push([x, z]);
    return true;
  };

  for (let t = 0; t < 100 && pts.length === 0; t++) tryAdd(bb.minX + rng.nextFloat() * w, bb.minZ + rng.nextFloat() * h);
  while (active.length) {
    const ai = rng.nextInt(active.length);
    const [px, pz] = pts[active[ai]!]!;
    let placed = false;
    for (let s = 0; s < k; s++) {
      const ang = rng.nextFloat() * Math.PI * 2, rad = r * (1 + rng.nextFloat());
      const nx = px + Math.cos(ang) * rad, nz = pz + Math.sin(ang) * rad;
      if (nx < bb.minX || nx > bb.maxX || nz < bb.minZ || nz > bb.maxZ) continue;
      if (tryAdd(nx, nz)) { placed = true; break; }
    }
    if (!placed) { active[ai] = active[active.length - 1]!; active.pop(); }
  }
  return pts;
}

/** Every tree point for a city: Poisson scatter per green polygon + explicit trees. */
export function treePoints(city: IntermediateCity, seed: number | string | bigint): Pt[] {
  const rng = new Rng(seed).fork(S_TREES);
  const r = Math.max(2, Math.round(6 / city.scale)); // 6 m spacing (ATL-4)
  const out: Pt[] = [...city.trees];
  for (const poly of city.green) {
    const bb = boundsOf(poly.points);
    for (const p of poissonDisk(bb, r, rng, (x, z) => pointInPolygonWithHoles(x, z, poly))) out.push(p);
  }
  return out;
}

/** Per-tree canopy jitter (±1), deterministic from the tree's position. */
export function treeShapeRng(x: number, z: number): Rng {
  return new Rng(`${S_TREE_SHAPE}:${Math.round(x)}:${Math.round(z)}`);
}
